// Use CustomInput when the page manages its own state (useState) without react-hook-form.
// It expects a controlled `value` prop and an `onChange(value: string)` callback.
// If the page uses useForm() from react-hook-form, use FormInput instead.
import React from 'react';

interface CustomProps {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'number' | 'date';
  required?: boolean;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  maskToNumeric?: boolean;
  maskToAlphanumeric?: boolean;
  maskToDecimal?: boolean;
  maxLength?: number;
  className?: string;
  errorAction?: React.ReactNode;
  tooltip?: string;
  labelPrefix?: string;
}

type CustomInputProps = CustomProps & Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof CustomProps>;

const CustomInput = ({
  name,
  label: labelText,
  type = 'text',
  required = false,
  value,
  onChange,
  error,
  disabled = false,
  maskToNumeric = false,
  maskToAlphanumeric = false,
  maskToDecimal = false,
  maxLength,
  className,
  errorAction,
  tooltip,
  labelPrefix,
  ...props
}: CustomInputProps) => {
  const safeId = name.trim().replace(/\s+/g, '_');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    if (maskToNumeric) {
      val = val.replace(/\D/g, '');
      if (maxLength && val.length > maxLength) {
        val = val.slice(0, maxLength);
      }
    }

    if (maskToAlphanumeric) {
      val = val.replace(/[^a-zA-Z0-9 ]/g, '');
    }

    if (maskToDecimal) {
      val = val.replace(/[^\d.]/g, '');
      const parts = val.split('.');
      if (parts.length > 2) {
        val = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    onChange(val);
  };

  return (
    <div className="form-group">
      <div className="form-floating">
        <input
          id={safeId}
          type={type}
          className={className ? `form-control ${className}` : 'form-control'}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          maxLength={maxLength}
          {...props}
          placeholder=" "
        />
        <label
          htmlFor={safeId}
          className={labelPrefix ? 'flex items-center gap-1.5' : undefined}
        >
          {labelPrefix && (
            <span className="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-on-surface-tertiary dark:text-on-surface-secondary flex-shrink-0">
              {labelPrefix}
            </span>
          )}
          {labelText}
          {required && (
            <span className="text-red-500 dark:text-red-400 ml-1">*</span>
          )}
          {tooltip && (
            <span className="ml-1 relative group cursor-help text-on-surface-quaternary">
              &#9432;
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-on-surface text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                {tooltip}
              </span>
            </span>
          )}
        </label>
      </div>

      {(error || errorAction) && (
        <div className="inline-flex items-center gap-1">
          {error && (
            <p role="alert" className="text-danger whitespace-nowrap">
              {error}
            </p>
          )}
          {errorAction && <div className="flex-shrink-0">{errorAction}</div>}
        </div>
      )}
    </div>
  );
};

export default CustomInput;
